package uz.mint.frontend.api

import kotlinx.browser.window
import kotlinx.coroutines.await
import uz.mint.frontend.SiteConfig
import uz.mint.frontend.i18n.Locale

/**
 * The destination picker's data: every country and every multi-country bundle on sale.
 *
 * This is the storefront's first screen. The old tabs — локальные / региональные / глобальные —
 * asked the customer to classify their own trip against the supplier's taxonomy before they could
 * see a single price; somebody flying to Türkiye knows the country, not the bundle type.
 *
 * Same client conventions as [CatalogApi]: fetched in the browser because the pages are statically
 * exported and prices change without a redeploy, parsed through `external interface` shapes that
 * are validated at the boundary so a malformed payload throws here rather than rendering
 * `undefined` into a card.
 */
object DestinationApi {

    suspend fun fetchDestinations(locale: Locale): Destinations {
        val raw = parse<DestinationsJson>(get("/api/catalog/destinations?lang=${locale.code}"))

        val countries = (raw.countries ?: throw CatalogException("Destinations are missing 'countries'"))
            .map { it.toDestination(DestinationKind.COUNTRY) }
        val regions = (raw.regions ?: throw CatalogException("Destinations are missing 'regions'"))
            .map { it.toDestination(DestinationKind.REGION) }

        return Destinations(countries = countries, regions = regions)
    }

    private suspend fun get(path: String): String {
        val response = window.fetch("${SiteConfig.apiOrigin}$path").await()
        if (!response.ok) {
            throw CatalogException("Destination request failed with HTTP ${response.status}")
        }
        return response.text().await()
    }

    private fun <T> parse(body: String): T = try {
        JSON.parse<T>(body)
    } catch (e: Throwable) {
        throw CatalogException("Destination response was not valid JSON: ${e.message}")
    }
}

enum class DestinationKind { COUNTRY, REGION }

/**
 * One place a customer can buy for — a country or a multi-country bundle.
 *
 * Both are one type on purpose: the grid renders them identically and the only thing that differs
 * is which query parameter narrows the catalogue, which [queryParam] answers. Two types would mean
 * two nearly identical card composables that could drift.
 */
data class Destination(
    /**
     * ISO alpha-2 for a country, the **exact region name** for a bundle.
     *
     * Not a slug for regions, and that is not laziness: the catalogue carries both
     * `Asia (20 areas)` and `Asia (20+ areas)`, which slugify identically, so a slug scheme merges
     * two different bundles at two different prices into one card.
     */
    val id: String,
    val kind: DestinationKind,
    val name: String,
    val planCount: Int,
    /** Cheapest plan here, in so'm; null when no FX rate is configured, exactly like a plan card. */
    val fromPriceUzs: Double?,
    /** Countries the landing page features. Always false for regions. */
    val popular: Boolean,
    /** Position in the curated list, or null when not featured. See [Destinations.popular]. */
    val popularRank: Int?,
    /** Worldwide bundles, so the picker can group them after the continental ones. */
    val global: Boolean,
) {
    /** `country=TR` or `region=Europe (30+ areas)` — the filter that narrows the catalogue here. */
    val queryParam: Pair<String, String>
        get() = when (kind) {
            DestinationKind.COUNTRY -> "country" to id
            DestinationKind.REGION -> "region" to id
        }
}

/**
 * This destination's address in the catalogue — `/plans?country=TR`, `/uz/plans?region=…`.
 *
 * One implementation, used both to build the landing page's links and to read the choice back out
 * of the URL on `/plans`. Two copies would drift, and a link that writes a parameter the page does
 * not read is a card that silently lands on the unfiltered catalogue.
 */
fun Destination.pathIn(locale: Locale): String {
    val (name, value) = queryParam
    return "${locale.plansPath}?$name=${encodeURIComponent(value)}"
}

data class Destinations(
    val countries: List<Destination>,
    val regions: List<Destination>,
) {
    val all: List<Destination> get() = countries + regions

    /**
     * The landing page's short list, **in the curated order** rather than the alphabetical one the
     * full list uses. Türkiye leads because it was put first, not because of where it sorts.
     */
    val popular: List<Destination>
        get() = countries.filter { it.popular }.sortedBy { it.popularRank ?: Int.MAX_VALUE }

    /**
     * Case-insensitive substring over the localised name, plus the bare country code.
     *
     * Substring rather than prefix here, unlike the server's country-name resolution: this filters
     * a list already on screen, where "корея" should find "Южная Корея" without the customer
     * having to know it does not start with that word.
     */
    fun search(query: String): Destinations {
        val q = query.trim().lowercase()
        if (q.isEmpty()) return this
        fun matches(d: Destination) = d.name.lowercase().contains(q) || d.id.lowercase() == q
        return Destinations(countries.filter(::matches), regions.filter(::matches))
    }

    val isEmpty: Boolean get() = countries.isEmpty() && regions.isEmpty()
}

private external interface DestinationsJson {
    val countries: Array<CountryJson>?
    val regions: Array<RegionJson>?
}

private external interface CountryJson {
    val code: String?
    val name: String?
    val planCount: Int?
    val fromPriceUzs: Double?
    val popular: Boolean?
    val popularRank: Int?
}

private external interface RegionJson {
    /** The key: the supplier's exact English label, which every filter is written against. */
    val region: String?
    /** The same label in the requested language. Absent on a backend older than this build. */
    val name: String?
    val planCount: Int?
    val fromPriceUzs: Double?
    val global: Boolean?
}

private fun CountryJson.toDestination(kind: DestinationKind) = Destination(
    id = code ?: throw CatalogException("Destination is missing 'code'"),
    kind = kind,
    name = name ?: throw CatalogException("Destination is missing 'name'"),
    planCount = planCount ?: 0,
    fromPriceUzs = fromPriceUzs,
    popular = popular ?: false,
    popularRank = popularRank,
    global = false,
)

private fun RegionJson.toDestination(kind: DestinationKind): Destination {
    // Read once into a local: `region` is an external interface property, so the compiler cannot
    // assume two reads return the same thing and will not smart-cast it.
    val key = region ?: throw CatalogException("Destination is missing 'region'")
    // The label is translated, the key is not — «Европа (30+ стран)» on the card, `Europe (30+
    // areas)` in the URL. Falling back to the key keeps a page exported against an older backend
    // readable rather than blank; this page is a static export and can outlive its API.
    val label = name?.takeIf { it.isNotBlank() } ?: key

    return Destination(
        id = key,
        kind = kind,
        name = label,
        planCount = planCount ?: 0,
        fromPriceUzs = fromPriceUzs,
        popular = false,
        popularRank = null,
        global = global ?: false,
    )
}
